/**
 * Inbox view-model — the shared shapes + per-kind display logic the
 * email-style approvals inbox renders from.
 *
 * The inbox is the founder's one place for flagged actions. Today every
 * item is an approval row from `approvals.list`; the shapes here are kept
 * deliberately narrow to that wire row so a future notifications feed can
 * be folded in as a second source without reshaping the list UI.
 *
 * Pure presentation helpers only — no network, no React. The money/date
 * formatting reuses `@/lib/format` so the inbox reads in the same en-IN
 * idiom as the rest of the workspace.
 */
import { paiseToInr } from "../../lib/format.ts";

/** One inbox item — mirrors a single `approvals.list` wire row. */
export interface InboxItem {
  id: string;
  kind: string;
  summary: string;
  entityType: string;
  entityId: string;
  requestPayload: Record<string, unknown>;
  status: "pending" | "approved" | "rejected";
  requestedByActorId: string;
  requestedByActorType: string;
  decidedByUserName: string | null;
  decisionNote: string | null;
  decidedAt: string | null;
  createdAt: string;
}

export type InboxStatus = InboxItem["status"];

/** Status filter chips — map 1:1 to the `approvals.list` `status` input. */
export const STATUS_FILTERS: Array<{ id: InboxStatus; label: string }> = [
  { id: "pending", label: "Needs approval" },
  { id: "approved", label: "Approved" },
  { id: "rejected", label: "Rejected" },
];

/** Human label for an approval kind (falls back to a de-snaked string). */
export const KIND_LABELS: Record<string, string> = {
  void_invoice: "Void invoice",
  over_discount_invoice: "Over-discount",
  over_credit_limit_invoice: "Over credit limit",
  write_off_invoice: "Write off",
  automated_send: "Automated send",
  vendor_payment_above_threshold: "Payment over limit",
  payroll_run_above_threshold: "Payroll over limit",
};

export function kindLabel(kind: string): string {
  return KIND_LABELS[kind] ?? kind.replace(/_/g, " ");
}

/**
 * Per-kind category — drives the row's left accent rail + the category
 * pill. `tone` matches the prototype's amber (needs-a-look) / rose
 * (money-or-risk) / sky (informational) accents.
 */
export type CategoryTone = "warn" | "bad" | "info";

export function kindCategory(kind: string): { label: string; tone: CategoryTone } {
  switch (kind) {
    case "vendor_payment_above_threshold":
      return { label: "Payment", tone: "bad" };
    case "payroll_run_above_threshold":
      return { label: "Payroll", tone: "bad" };
    case "write_off_invoice":
      return { label: "Write-off", tone: "bad" };
    case "void_invoice":
      return { label: "Void", tone: "warn" };
    case "over_discount_invoice":
      return { label: "Discount", tone: "warn" };
    case "over_credit_limit_invoice":
      return { label: "Credit", tone: "warn" };
    case "automated_send":
      return { label: "Send", tone: "info" };
    default:
      return { label: "Approval", tone: "warn" };
  }
}

/** A short, human label for the requester actor (we don't get their name on the wire). */
export function requesterLabel(item: Pick<InboxItem, "requestedByActorType">): string {
  switch (item.requestedByActorType) {
    case "user":
      return "Team member";
    case "system":
      return "Flint";
    default:
      return item.requestedByActorType.replace(/_/g, " ");
  }
}

/** Two-letter avatar seed for the requester chip. */
export function requesterInitials(item: Pick<InboxItem, "requestedByActorType">): string {
  return item.requestedByActorType === "system" ? "L" : "TM";
}

/** A short snapshot row: the facts a founder needs to act without opening. */
export interface SnapshotChip {
  label: string;
  value: string;
  /** Render as the emphasised dark money chip. */
  strong?: boolean;
}

/**
 * Snapshot chips derived from an approval's `requestPayload`. The same
 * per-kind facts the old card showed, but shaped as chips for the row and
 * the reading pane. Falls back to an empty list when nothing is known.
 */
export function snapshotChips(kind: string, payload: Record<string, unknown>): SnapshotChip[] {
  const str = (k: string): string | null => (typeof payload[k] === "string" ? (payload[k] as string) : null);
  const inr = (k: string): string | null => {
    const v = payload[k];
    if (typeof v !== "string" || !/^-?\d+$/.test(v)) return null;
    return paiseToInr(v);
  };

  switch (kind) {
    case "vendor_payment_above_threshold": {
      const chips: SnapshotChip[] = [];
      const gross = inr("grossPaise");
      if (gross) chips.push({ label: "Amount", value: gross, strong: true });
      const mode = str("mode");
      if (mode) chips.push({ label: "Mode", value: mode.toUpperCase() });
      const paidOn = str("paidOn");
      if (paidOn) chips.push({ label: "Paid on", value: paidOn });
      const ref = str("reference");
      if (ref) chips.push({ label: "Ref", value: ref });
      return chips;
    }
    case "payroll_run_above_threshold": {
      const chips: SnapshotChip[] = [];
      const net = inr("netPaise");
      if (net) chips.push({ label: "Net payable", value: net, strong: true });
      const period = str("periodMonth");
      if (period) chips.push({ label: "Period", value: period.slice(0, 7) });
      const count = payload.employeeCount;
      if (typeof count === "number") chips.push({ label: "Employees", value: String(count) });
      return chips;
    }
    case "over_credit_limit_invoice": {
      const chips: SnapshotChip[] = [];
      const draft = inr("draftTotalPaise");
      if (draft) chips.push({ label: "Draft total", value: draft, strong: true });
      const limit = inr("creditLimitPaise");
      if (limit) chips.push({ label: "Credit limit", value: limit });
      const outstanding = inr("outstandingPaise");
      if (outstanding) chips.push({ label: "Outstanding", value: outstanding });
      return chips;
    }
    case "void_invoice":
    case "over_discount_invoice": {
      const reason = str("reason");
      return reason ? [{ label: "Reason", value: reason }] : [];
    }
    default:
      return [];
  }
}

/**
 * Why the founder is being asked — a one-line rationale per kind, shown in
 * the reading-pane callout. Deterministic copy (principle 3: code computes,
 * AI narrates — this is fixed product copy, not generated).
 */
export function whyText(kind: string): string {
  switch (kind) {
    case "void_invoice":
      return "Voiding a sent invoice reverses recognised sales and removes it from the GST return. Append-only — a void creates a reversing entry, it doesn't delete the original.";
    case "vendor_payment_above_threshold":
      return "This pay-out is above the company's approval threshold. Approving releases the payment and posts the TDS withheld at the recorded date.";
    case "payroll_run_above_threshold":
      return "This payroll run's total net pay is above the company's approval threshold. Approving lets the operator pay each employee's net to the bank; the run stays append-only.";
    case "over_credit_limit_invoice":
      return "Issuing this invoice would take the customer past their credit limit. Approve to issue anyway, or reject to hold it.";
    case "over_discount_invoice":
      return "A line discount exceeds the policy ceiling. Approve to let the discount stand on the issued invoice.";
    case "write_off_invoice":
      return "Writing this off books it as bad debt. Founder-only — approving is final and lands on the audit trail.";
    case "automated_send":
      return "An automated customer message was held by the send guardrails. Approve to let it go out.";
    default:
      return "This action was flagged for a Founder decision before it can proceed.";
  }
}
